import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CreateStartupFormValues } from '../schema';
import { migrateLegacyDraftValues } from './legacy-draft';

export interface WizardDraft {
  step: number;
  values: Partial<CreateStartupFormValues>;
}

const EMPTY_DRAFT: WizardDraft = { step: 0, values: {} };

/** True once a draft holds anything worth resuming (vs. the untouched default). */
export function hasInProgressDraft(draft: WizardDraft | undefined): boolean {
  return Boolean(draft && (draft.step > 0 || Object.keys(draft.values).length > 0));
}

interface CreateStartupStoreState {
  drafts: Record<string, WizardDraft>;
  setStep: (userId: string, step: number) => void;
  setValues: (userId: string, values: Partial<CreateStartupFormValues>) => void;
  reset: (userId: string) => void;
}

/**
 * Persists in-progress create-startup wizard state to localStorage, keyed by
 * the owning user's id, so a refresh, an accidental navigation away, or a
 * logout/login doesn't lose a founder's progress.
 *
 * Keyed per-user (`drafts[userId]`), not a single shared slot - on a shared/
 * kiosk machine, each user only ever reads and writes their own entry, so
 * one user's in-progress company name, financials, and founder data is never
 * shown to a different user who logs into the same browser afterward. A
 * draft is only removed by `reset(userId)`, called on a successful create
 * (see `CreateStartupForm`) - never on logout, since the whole point of
 * keying by user is that the SAME user gets their own draft back after
 * logging back in.
 *
 * Never persists `otpCode`-shaped or auth-adjacent fields (there are none
 * left on this form post-OTP-removal) - if a sensitive field is ever added
 * to this schema, add a `partialize` option to the `persist` config below to
 * exclude it, rather than persisting the whole `values` object unfiltered.
 */
export const useCreateStartupStore = create<CreateStartupStoreState>()(
  persist(
    (set) => ({
      drafts: {},
      setStep: (userId, step) =>
        set((state) => ({
          drafts: {
            ...state.drafts,
            [userId]: { ...(state.drafts[userId] ?? EMPTY_DRAFT), step },
          },
        })),
      setValues: (userId, values) =>
        set((state) => {
          const existing = state.drafts[userId] ?? EMPTY_DRAFT;
          return {
            drafts: {
              ...state.drafts,
              [userId]: { ...existing, values: { ...existing.values, ...values } },
            },
          };
        }),
      reset: (userId) =>
        set((state) => {
          const drafts = { ...state.drafts };
          delete drafts[userId];
          return { drafts };
        }),
    }),
    {
      name: 'create-startup-wizard',
      // Bump this whenever CreateStartupFormValues changes shape, and give
      // `migrate` real per-version logic at that point (drop/rename fields
      // as needed) instead of this no-op - without an explicit version,
      // zustand silently defaults to 0 and would spread stale, unfiltered
      // persisted data into `defaultValues` after a schema change.
      //
      // v1 -> v2: the store moved from a single shared {step, values} slot to
      // per-user `drafts`. A v1 draft has no owner id to key it by, so it is
      // discarded rather than guessed at - never attribute an untagged draft
      // to a user.
      //
      // v2 -> v3: self-identification answers moved from booleans to
      // 'yes'/'no'/'prefer_not_to_say', and hasLeadInvestor became an explicit
      // choice (UX sheet r113-r115, r151) - see migrateLegacyDraftValues.
      version: 3,
      migrate: (persisted, version) => {
        if (version < 2) return { drafts: {} };
        const { drafts = {} } = (persisted ?? {}) as { drafts?: Record<string, WizardDraft> };
        return {
          drafts: Object.fromEntries(
            Object.entries(drafts).map(([userId, draft]) => [
              userId,
              { ...draft, values: migrateLegacyDraftValues(draft.values ?? {}) },
            ]),
          ),
        };
      },
    },
  ),
);
